import {
	Button,
	CircularProgress,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	TextField,
	useTheme,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import * as React from "react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { ErrorLabel } from "../../../../../controls/error-label";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { ShiftParameters } from "../../models/ShiftParameters";
import { upsertShiftParameters } from "../../repositories/ShiftsCrewsRepository";

type Props = {
	shiftSelected: ShiftParameters | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
	assetId: number | null;
	assetName: string;
};

export const EditShift = ({
	shiftSelected,
	show,
	onHide,
	suffixTitle,
	assetId,
	assetName,
}: Props) => {
	const theme = useTheme();

	const [open, setOpen] = useState(false);
	const [error, setError] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [
		initialShiftPerDayAndCrewsNumber,
		setInitialShiftPerDayAndCrewsNumber,
	] = useState(false);

	const {
		control,
		setValue,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<ShiftParameters>({ defaultValues: INITIAL_VALUES });

	// Progress bar
	const calculatePercentage = () =>
		((crewRotation?.length as number) / maxCrewRotationLength) * 100;

	const createShift = useMutation({
		mutationFn: ({
			shiftData,
			isUpdate,
		}: { shiftData: ShiftParameters; isUpdate: boolean }) =>
			upsertShiftParameters(shiftData, assetId, isUpdate),
		onSuccess: () => {
			setOpen(true);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const onSubmit: SubmitHandler<ShiftParameters> = async (data) => {
		setIsSubmitLoading(true);

		await createShift.mutate({ shiftData: data, isUpdate: true });
	};

	const handleClose = (
		event?: React.SyntheticEvent | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	// const patternStart = watch('PatternStart');
	const shiftsPerDay = watch("ShiftsPerDay");
	const shiftCodes = watch("ShiftCodes");
	const numberOfCrews = watch("NumberOfCrews");
	const crewsCodes = watch("Crews");
	const rotationPattern = watch("PatternDays");
	const comments = watch("Comments");
	const crewRotation = watch("CrewRotation");
	const maxCrewRotationLength = shiftsPerDay * rotationPattern;

	useEffect(() => {
		if (show) {
			reset();
			setValue("PatternStart", shiftSelected?.PatternStart ?? new Date());
			setValue("ShiftCodes", shiftSelected?.ShiftCodes ?? "");
			setValue("Crews", shiftSelected?.Crews ?? "");
			setValue("PatternDays", shiftSelected?.PatternDays ?? 0);
			setValue("CrewRotation", shiftSelected?.CrewRotation ?? "");
			setValue("Comments", shiftSelected?.Comments ?? "");
			setInitialShiftPerDayAndCrewsNumber(!initialShiftPerDayAndCrewsNumber);
		}
	}, [show]);

	useEffect(() => {
		setValue("ShiftsPerDay", crewRotation.length / rotationPattern);
		setValue("NumberOfCrews", crewsCodes.length);
	}, [initialShiftPerDayAndCrewsNumber]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Edit Shift${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<MesfModal
				title="EDIT SHIFT"
				open={show}
				handleClose={() => onHide(false)}
				id="edit-shift-modal"
				maxWidth="md"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content>
						<Grid2 container spacing={2} p={1}>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<TextField
									label="Asset"
									value={assetName}
									disabled
									fullWidth
									variant="outlined"
									margin="dense"
								/>
							</Grid2>
							<Controller
								name="PatternStart"
								control={control}
								render={({ field: { value, onChange } }) => (
									<LocalizationProvider dateAdapter={AdapterMoment}>
										<Grid2 size={{ md: 6, xs: 12 }}>
											<DatePicker
												format="MM/DD/YYYY"
												sx={{
													minWidth: "100%",
													borderTopLeftRadius: 3,
													borderTopRightRadius: 3,
												}}
												label="Start"
												value={value ? moment(value) : value}
												onChange={(evt: any) => onChange(evt?.toDate())}
												slotProps={{
													textField: {
														fullWidth: true,
														variant: "standard",
													},
												}}
											/>
										</Grid2>
										<Grid2 size={{ md: 6, xs: 12 }}>
											<DatePicker
												label="Time"
												value={value ? moment(value) : value}
												onChange={(evt: any) => onChange(evt?.toDate())}
												slotProps={{
													textField: {
														fullWidth: true,
														variant: "standard",
													},
												}}
											/>
										</Grid2>
									</LocalizationProvider>
								)}
							/>

							<Grid2 size={{ md: 6, xs: 6 }}>
								<Controller
									name="ShiftsPerDay"
									control={control}
									rules={{
										min: 1,
									}}
									render={({ field: { onChange, value } }) => (
										<TextField
											label={`# ${
												shiftsPerDay > 1 ? "Shifts" : "Shift"
											}  per day`}
											variant="outlined"
											error={!!errors.ShiftsPerDay}
											fullWidth
											margin="dense"
											autoComplete="off"
											type="number"
											value={value}
											onChange={
												// FIXME: límite de 10?
												(evt: any) =>
													parseInt(evt.target.value) >= 1 &&
													parseInt(evt.target.value) <= 10 &&
													onChange(evt)
											}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 6 }}>
								<Controller
									name="ShiftCodes"
									control={control}
									rules={{
										required: `Please put ${shiftsPerDay} shifts`,
										validate: (value) => value.length == shiftsPerDay,
									}}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											label="Shifts Ids"
											variant="outlined"
											error={!!error}
											helperText={error?.message}
											fullWidth
											margin="dense"
											autoComplete="off"
											value={value}
											onChange={(evt: any) => {
												// TODO: aquí deberíamos prevenir, que no pueda haber dos valores iguales, . Ej. dos DNN
												if (evt.target.value.length <= shiftsPerDay)
													onChange(evt.target.value.toString().toUpperCase());
											}}
										/>
									)}
								/>
								{shiftCodes.length < shiftsPerDay &&
									!!errors.ShiftCodes === false && (
										<ErrorLabel
											text={`Insert: ${shiftsPerDay} ${
												shiftsPerDay == 1 ? "Id" : "Ids"
											}. ${
												shiftsPerDay > 1 ? "The Ids must be differents" : ""
											}`}
										/>
									)}
							</Grid2>
							<Grid2 size={{ md: 6, xs: 6 }}>
								<Controller
									name="NumberOfCrews"
									control={control}
									rules={{
										required: "# Crews day is required",
										min: 1,
										max: 9,
									}}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											label={`# ${numberOfCrews > 1 ? "Crews" : "Crew"}`}
											variant="outlined"
											error={!!error}
											helperText={error?.message}
											fullWidth
											margin="dense"
											autoComplete="off"
											type="number"
											value={value}
											onChange={(evt: any) => {
												if (
													parseInt(evt.target.value) >= 1 &&
													parseInt(evt.target.value) <= 9
												)
													onChange(evt);
											}}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 6 }}>
								<Controller
									name="Crews"
									control={control}
									rules={{
										required: `Please put ${numberOfCrews} crews`,
										validate: (value) => value.length == numberOfCrews,
									}}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											label="Crews Ids"
											variant="outlined"
											error={!!error}
											helperText={error?.message}
											disabled={numberOfCrews <= 0}
											fullWidth
											margin="dense"
											autoComplete="off"
											value={value}
											onChange={(evt: any) => {
												if (evt.target.value.length <= numberOfCrews)
													onChange(evt.target.value.toString().toUpperCase());
											}}
										/>
									)}
								/>
								{crewsCodes.length < numberOfCrews &&
									!!errors.Crews === false && (
										<ErrorLabel
											text={`Insert: ${numberOfCrews} ${
												numberOfCrews == 1 ? "Crew" : "Crews"
											}. ${
												numberOfCrews > 1 ? "The Crews must be differents" : ""
											}`}
										/>
									)}
							</Grid2>
							<Grid2 size={{ md: 6, xs: 6 }}>
								<Controller
									name="PatternDays"
									control={control}
									// rules={{
									//     required: true
									// }}
									render={({ field: { value, onChange } }) => (
										<TextField
											label="Rotation Pattern"
											variant="outlined"
											error={!!errors.PatternDays}
											fullWidth
											type="number"
											margin="dense"
											autoComplete="off"
											value={value}
											onChange={
												(evt: any) => {
													// FIXME: consultar si estos valores están bien, son los limites del sP
													if (
														parseInt(evt.target.value) >= 5 &&
														parseInt(evt.target.value) <= 60
													)
														onChange(evt);
												}
												//TODO: SUMAR UN HELPER: que diga: introduce a value betwen 5 and 60
											}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									name="CrewRotation"
									control={control}
									rules={{
										validate: (value) => value.length == maxCrewRotationLength,
									}}
									render={({ field: { value, onChange } }) => (
										<TextField
											value={value}
											onChange={(evt: any) => {
												if (evt.target.value.length <= maxCrewRotationLength)
													onChange(evt.target.value.toString().toUpperCase());
											}}
											error={!!errors.CrewRotation}
											label="Crew rotation"
											variant="outlined"
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									)}
								/>
								{
									<Grid2 container height={2}>
										<Grid2
											style={{
												height: 2,
												width: `${calculatePercentage()}% `,
												backgroundColor: `${
													crewRotation.length == maxCrewRotationLength
														? "#4CAF50"
														: "#F44336"
												}`,
												transition: " 0.2s ease",
												margin: "0 5px",
											}}
										/>
									</Grid2>
								}
								<Grid2 container>
									<Grid2>
										{(crewRotation?.length as number) >= 1 &&
											(crewRotation?.length as number) !==
												maxCrewRotationLength && (
												<span
													style={{
														fontSize: 12,
														color: theme.palette.info.main,
														marginLeft: 5,
													}}
												>{`${crewRotation?.length}/${maxCrewRotationLength}`}</span>
											)}
									</Grid2>
								</Grid2>
								{!!errors.CrewRotation && (
									<ErrorLabel
										text={`Please put ${maxCrewRotationLength} crews`}
									/>
								)}
								{numberOfCrews > 0 &&
									Number.isInteger(maxCrewRotationLength / numberOfCrews) ===
										false && (
										<Grid2 container style={{ padding: "0", marginLeft: 5 }}>
											<ErrorLabel
												text={`Rotation # not a multiple of Shifts (${maxCrewRotationLength}) / Crews (${numberOfCrews})`}
											/>
										</Grid2>
									)}
							</Grid2>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									name="Comments"
									control={control}
									render={({ field: { value, onChange } }) => (
										<TextField
											style={{ margin: "8px 0" }}
											size="medium"
											maxRows={5}
											label="Comments"
											multiline
											variant="outlined"
											fullWidth
											autoComplete="off"
											value={value}
											onChange={(evt: any) => {
												if (evt.target.value.length <= 200) onChange(evt);
											}}
										/>
									)}
								/>
								<Grid2 container style={{ marginBottom: 18 }}>
									<Grid2>
										{(comments?.length as number) >= 1 &&
											(comments?.length as number) !== 200 && (
												<span
													style={{
														fontSize: 12,
														color: theme.palette.info.main,
														marginLeft: 5,
													}}
												>{`${comments?.length}/200`}</span>
											)}
										{(comments?.length as number) == 200 && (
											<span
												style={{
													fontSize: 12,
													color: theme.palette.info.main,
													marginLeft: 5,
												}}
											>
												Max. 200
											</span>
										)}
									</Grid2>
								</Grid2>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={open}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={handleClose}>
					The shift was created successfully
				</Alert>
			</Snackbar>
		</>
	);
};
